import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { GROUP_ICON_NAMES, GroupIcon } from "@/lib/groupIcons";

const daysLeft = (m) => (m.earliestExpiration ? Math.floor((new Date(m.earliestExpiration) - new Date()) / 86400000) : null);

export default function GroupDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = ["owner", "admin", "reviewer"].includes(user?.role);
  const [group, setGroup] = useState(null);
  const [allInsureds, setAllInsureds] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [iconOpen, setIconOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState(false);

  const load = () => api.get(`/groups/${id}`).then((r) => setGroup(r.data));
  const loadInsureds = () => api.get("/vendors").then((r) => setAllInsureds(r.data));
  useEffect(() => { load().catch(() => { toast.error("We couldn’t load this group."); navigate("/groups"); }); loadInsureds().catch(() => {}); }, [id]);

  if (!group) return <div className="h-40 animate-pulse rounded-lg border border-slate-200 bg-white" data-testid="group-detail-loading" />;

  const memberIds = new Set((group.members || []).map((m) => m.id));

  async function saveName() {
    const name = nameDraft.trim();
    if (!name) return;
    try { await api.patch(`/groups/${id}`, { name }); setEditingName(false); toast.success("Group renamed."); load(); }
    catch (error) { toast.error(formatApiError(error)); }
  }
  async function pickIcon(name) {
    setIconOpen(false);
    setGroup((prev) => ({ ...prev, icon: name }));
    try { await api.patch(`/groups/${id}`, { icon: name }); }
    catch (error) { toast.error(formatApiError(error)); load(); }
  }
  async function toggleDashboard(next) {
    setGroup((prev) => ({ ...prev, showOnDashboard: next }));
    try { await api.patch(`/groups/${id}`, { showOnDashboard: next }); toast.success(next ? "Pinned to dashboard." : "Removed from dashboard."); }
    catch (error) { toast.error(formatApiError(error)); load(); }
  }
  async function addMember(vendorId) {
    try { await api.post(`/groups/${id}/members`, { vendorId }); toast.success("Insured added to group."); load(); }
    catch (error) { toast.error(formatApiError(error)); }
  }
  async function removeMember(vendorId) {
    try { await api.delete(`/groups/${id}/members/${vendorId}`); toast.success("Insured removed from group."); load(); }
    catch (error) { toast.error(formatApiError(error)); }
  }
  async function confirmDelete() {
    setPendingDelete(false);
    try { await api.delete(`/groups/${id}`); toast.success("Group deleted. No insureds were removed."); navigate("/groups"); }
    catch (error) { toast.error(formatApiError(error)); }
  }

  const addable = allInsureds.filter((v) => !memberIds.has(v.id) && `${v.name} ${v.email}`.toLowerCase().includes(addQuery.toLowerCase()));

  return (
    <section data-testid="group-detail-page">
      <Link className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline" data-testid="back-to-groups" to="/groups"><ArrowLeft size={15} />All groups</Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <button className="flex h-14 w-14 items-center justify-center rounded-lg bg-blue-50 text-blue-700 transition hover:bg-blue-100 disabled:opacity-100" data-testid="group-icon-button" disabled={!canWrite} onClick={() => canWrite && setIconOpen(true)} title={canWrite ? "Change icon" : ""} type="button">
            <GroupIcon name={group.icon} size={26} />
          </button>
          <div>
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input autoFocus className="h-9 w-64" data-testid="group-name-edit-input" onChange={(e) => setNameDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveName()} value={nameDraft} />
                <Button data-testid="group-name-save" onClick={saveName} size="icon" variant="ghost"><Check size={16} /></Button>
                <Button data-testid="group-name-cancel" onClick={() => setEditingName(false)} size="icon" variant="ghost"><X size={16} /></Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-slate-900" data-testid="group-detail-name">{group.name}</h1>
                {canWrite && <Button data-testid="group-name-edit" onClick={() => { setNameDraft(group.name); setEditingName(true); }} size="icon" variant="ghost"><Pencil size={15} /></Button>}
              </div>
            )}
            <p className="mt-1 text-sm text-slate-600" data-testid="group-detail-stats">{group.memberCount} insured{group.memberCount === 1 ? "" : "s"} · {group.compliantCount} compliant · {group.issuesCount} issue{group.issuesCount === 1 ? "" : "s"}</p>
          </div>
        </div>
        {canWrite && (
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700" htmlFor="dash-toggle">
              <Switch checked={!!group.showOnDashboard} data-testid="group-dashboard-toggle" id="dash-toggle" onCheckedChange={toggleDashboard} />
              Show on dashboard
            </label>
            <Button className="text-red-600 hover:text-red-700" data-testid="group-delete-button" onClick={() => setPendingDelete(true)} variant="outline"><Trash2 size={15} />Delete group</Button>
          </div>
        )}
      </div>

      <div className="mt-7 rounded-lg border bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <h2 className="font-semibold text-slate-900">Members</h2>
          {canWrite && <Button data-testid="add-member-button" onClick={() => { setAddQuery(""); setAddOpen(true); }} size="sm"><Plus size={15} />Add insured</Button>}
        </div>
        {group.members?.length ? (
          <Table data-testid="group-members-table">
            <TableHeader><TableRow><TableHead>Insured</TableHead><TableHead>Status</TableHead><TableHead>Expires</TableHead><TableHead className="w-20">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {group.members.map((m) => (
                <TableRow data-testid={`member-row-${m.id}`} key={m.id}>
                  <TableCell><Link className="font-semibold text-blue-700 hover:underline" to={`/insureds/${m.id}`}>{m.name}</Link><p className="text-xs text-slate-500">{m.email}</p></TableCell>
                  <TableCell><StatusBadge status={m.status} /></TableCell>
                  <TableCell>{(() => { const d = daysLeft(m); return d === null ? <span className="text-xs text-slate-400">No COI</span> : <span className={`text-sm ${d < 0 ? "font-medium text-red-600" : d <= 30 ? "font-medium text-amber-600" : "text-slate-700"}`}>{d < 0 ? `Expired ${-d}d ago` : `${d}d`}</span>; })()}</TableCell>
                  <TableCell>{canWrite && <Button aria-label={`Remove ${m.name} from group`} data-testid={`remove-member-${m.id}`} onClick={() => removeMember(m.id)} size="icon" variant="ghost"><X size={16} /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="grid min-h-40 place-items-center p-8 text-center" data-testid="group-members-empty"><div><h3 className="font-semibold text-slate-900">No insureds in this group yet</h3><p className="mt-1 text-sm text-slate-500">{canWrite ? "Add insureds to organize them here." : "Ask a teammate to add insureds."}</p></div></div>
        )}
      </div>

      {/* Icon picker */}
      <Dialog onOpenChange={setIconOpen} open={iconOpen}>
        <DialogContent data-testid="group-icon-dialog">
          <DialogHeader><DialogTitle>Choose an icon</DialogTitle><DialogDescription>Pick an icon that represents this group.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-6 gap-2 sm:grid-cols-7">
            {GROUP_ICON_NAMES.map((name) => (
              <button className={`flex h-11 w-11 items-center justify-center rounded-md border transition ${group.icon === name ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-blue-300"}`} data-testid={`icon-option-${name}`} key={name} onClick={() => pickIcon(name)} type="button">
                <GroupIcon name={name} size={19} />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add member */}
      <Dialog onOpenChange={setAddOpen} open={addOpen}>
        <DialogContent data-testid="add-member-dialog">
          <DialogHeader><DialogTitle>Add insureds to “{group.name}”</DialogTitle><DialogDescription>Search and add insureds to this group.</DialogDescription></DialogHeader>
          <Input data-testid="add-member-search" onChange={(e) => setAddQuery(e.target.value)} placeholder="Search insureds" value={addQuery} />
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {addable.length ? addable.map((v) => (
              <div className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2" data-testid={`addable-${v.id}`} key={v.id}>
                <div><p className="text-sm font-medium text-slate-900">{v.name}</p><p className="text-xs text-slate-500">{v.email}</p></div>
                <Button data-testid={`add-insured-to-group-${v.id}`} onClick={() => addMember(v.id)} size="sm" variant="outline"><Plus size={14} />Add</Button>
              </div>
            )) : <p className="py-6 text-center text-sm text-slate-500">{allInsureds.length ? "No matching insureds available to add." : "No insureds yet."}</p>}
          </div>
          <DialogFooter><Button onClick={() => setAddOpen(false)} variant="outline">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog onOpenChange={(o) => !o && setPendingDelete(false)} open={pendingDelete}>
        <DialogContent data-testid="group-delete-dialog">
          <DialogHeader><DialogTitle>Delete “{group.name}”?</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-600">Deleting this group will <span className="font-semibold text-slate-900">not delete or archive any insureds</span>. The {group.memberCount} insured{group.memberCount === 1 ? "" : "s"} in it will simply no longer be members. This can’t be undone.</DialogDescription>
          <DialogFooter><Button onClick={() => setPendingDelete(false)} variant="outline">Cancel</Button><Button className="bg-red-600 hover:bg-red-700" data-testid="group-delete-confirm" onClick={confirmDelete}>Delete group</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
